import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  packedOrderThunk,
  selectUnpackedOrders,
} from "../../store/actions/ordersSlice";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import { blue, grey, orange, red } from "@mui/material/colors";
import Card from "@mui/material/Card";
import {
  IconButtonBackgroundLightGrey,
  IconStackButtonFullHeight,
} from "../styled/button";
import Divider from "@mui/material/Divider";
import PackedConfirmationDialog from "../dialogs/orders/PackedConfirmationDialog";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import { Refresh } from "@mui/icons-material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { setIsFullscreen } from "../../store/actions/dashboardSlice";
import DashboardNoOrdersMessage from "./DashboardNoOrdersMessage";
import { NO_PACKING_ORDER_MESSAGE } from "../../types/order";
import ReceiptIcon from "@mui/icons-material/Receipt";
import Fade from "@mui/material/Fade";

const OrderTimer = ({ preparationStart }) => {
  const [elapsedTime, setElapsedTime] = useState("");
  const [timerColor, setTimerColor] = useState("default");

  const calculateElapsedTime = () => {
    const now = new Date();
    const startTime = new Date(preparationStart);
    const diffInMs = now - startTime;

    const hours = Math.floor(diffInMs / (1000 * 60 * 60));
    const minutes = Math.floor((diffInMs % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diffInMs % (1000 * 60)) / 1000);

    const formattedTime = `${hours.toString().padStart(2, "0")}:${minutes
      .toString()
      .padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;

    const totalMinutes = Math.floor(diffInMs / (1000 * 60));
    let color = "default";

    if (totalMinutes >= 27) {
      color = "error";
    } else if (totalMinutes >= 20) {
      color = "warning";
    } else {
      color = "info";
    }

    return { formattedTime, color };
  };

  useEffect(() => {
    const updateTimer = () => {
      const { formattedTime, color } = calculateElapsedTime();
      setElapsedTime(formattedTime);
      setTimerColor(color);
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);

    return () => clearInterval(interval);
  }, [preparationStart]);

  const getTimerBackgroundColor = () => {
    switch (timerColor) {
      case "error":
        return red[100];
      case "warning":
        return orange[100];
      case "info":
        return blue[100];
      default:
        return grey[100];
    }
  };

  const getTimerTextColor = () => {
    switch (timerColor) {
      case "error":
        return red[800];
      case "warning":
        return orange[800];
      case "info":
        return blue[800];
      default:
        return grey[800];
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 0.5,
        backgroundColor: getTimerBackgroundColor(),
        borderRadius: 1,
        padding: "4px 8px",
        minWidth: "80px",
      }}
    >
      <AccessTimeIcon
        sx={{
          fontSize: 14,
          color: getTimerTextColor(),
        }}
      />
      <Typography
        variant="caption"
        sx={{
          fontWeight: 600,
          color: getTimerTextColor(),
          fontFamily: "monospace",
        }}
      >
        {elapsedTime}
      </Typography>
    </Box>
  );
};

export default function Packing() {
  const dispatch = useDispatch();

  const orders = useSelector(selectUnpackedOrders);

  const isFullScreen = useSelector((state) => state.dashboard.isFullScreen);
  const alignment = useSelector((state) => state.dashboard.alignment);

  const [selectedOrder, setSelectedOrder] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const toggleFullscreen = () => {
    const shouldEnterFullscreen = !isFullScreen;

    if (shouldEnterFullscreen) {
      document.documentElement.requestFullscreen();
    } else if (document.fullscreenElement) {
      document.exitFullscreen();
    }

    dispatch(setIsFullscreen(shouldEnterFullscreen));
  };

  const handleOpenDialog = (order) => {
    setSelectedOrder(order);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setSelectedOrder(null);
    setDialogOpen(false);
  };

  const handleConfirmAction = () => {
    if (selectedOrder) {
      dispatch(packedOrderThunk({ order: selectedOrder }));
    }
    handleCloseDialog();
  };

  const reloadComponent = () => {
    setReloadKey((prev) => prev + 1);
  };

  const getOrderBackgroundColor = (order) => {
    if (order.lateStatus) {
      return red["600"];
    }
    if (order.isAlmostLate) {
      return orange["600"];
    }
    return grey["200"];
  };

  const getOrderTextColor = (order) => {
    if (order.lateStatus || order.isAlmostLate) {
      return grey["50"];
    }
    return grey["600"];
  };

  const renderOrderCard = (order) => {
    const backgroundColor = getOrderBackgroundColor(order);
    const textColor = getOrderTextColor(order);

    return (
      <Grid item xs={4} sm={3} md={2} key={order.id}>
        <IconStackButtonFullHeight
          padding={80}
          bgColor={backgroundColor}
          bgColorHover={grey["300"]}
          onClick={() => handleOpenDialog(order)}
        >
          <Card
            elevation={0}
            sx={{
              backgroundColor: "transparent",
              width: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Typography variant="h5" fontWeight={700} sx={{ color: textColor }}>
              {order.order_index}
            </Typography>

            {order.preparation_start && (
              <OrderTimer preparationStart={order.preparation_start} />
            )}
          </Card>
        </IconStackButtonFullHeight>
      </Grid>
    );
  };

  const renderHeader = () => {
    return (
      <Box display="flex" justifyContent="space-between">
        <Typography variant="h4" align="center">
          AGUARDANDO PRODUÇÃO
        </Typography>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <IconButtonBackgroundLightGrey onClick={toggleFullscreen}>
            {isFullScreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
          </IconButtonBackgroundLightGrey>

          <IconButtonBackgroundLightGrey
            onClick={reloadComponent}
            sx={{ ml: 1 }}
          >
            <Refresh />
          </IconButtonBackgroundLightGrey>
        </Box>
      </Box>
    );
  };

  const renderEmptyState = () => {
    return (
      <DashboardNoOrdersMessage
        title={"Nenhum pedido para embalagem"}
        body={NO_PACKING_ORDER_MESSAGE}
        icon={<ReceiptIcon />}
      />
    );
  };

  const renderOrdersGrid = () => {
    return (
      <Grid container spacing={2} sx={{ height: "100%" }}>
        {orders.map(renderOrderCard)}
      </Grid>
    );
  };

  const renderPackingContent = () => {
    return (
      <Box>
        {renderHeader()}
        <Divider sx={{ my: 2 }} />
        {orders.length === 0 ? renderEmptyState() : renderOrdersGrid()}
        <PackedConfirmationDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          onConfirm={handleConfirmAction}
          orderProducts={selectedOrder?.order_products}
          title={`Marcar pedido ${selectedOrder?.order_index} como embalado?`}
          description="Você deseja marcar este pedido como embalado?"
        />
      </Box>
    );
  };

  return (
    <Fade
      in={alignment === "packing"}
      mountOnEnter
      unmountOnExit
      key={reloadKey}
    >
      {renderPackingContent()}
    </Fade>
  );
}
